import { useEffect, useState } from 'react'
import * as React from 'react'

import { ScreenWrapper } from '../modules/common/components/ScreenWrapper'
import TemplateOrganization from '../interfaces/TemplateOrganization'
import { Rule, validateFields } from '../utils/validateForm'
import useTemplateFetcher from '../hooks/useTemplateFetcher'
import useTemplatePoster from '../hooks/useTemplatePoster'
import TemplateDto from '../interfaces/TemplateDto'
import { useAuth } from '../hooks/useAuth'
import { AppPath } from '../enums/AppPath'

import { ButtonAction, ButtonMenuSet } from './Buttons'
import TemplateConsignment from './TemplateConsignment'
import { RadioGroup, RadioItem } from './RadioGroup'
import { DisplayLabel } from './DisplayValue'
import TemplateDoc from './TemplateDoc'
import InputField from './InputField'
import { Info } from './MessageInfo'

import '@mdxeditor/editor/style.css'

const buttonCaptions = ['General', 'Consignaciones', 'Reservas', 'Cotizaciones', 'Nota de Venta']
const nombreCollection: RadioItem[] = [
  { label: 'Nombre de fantasía' },
  { label: 'Razón social' },
  { label: 'Personalizado' },
]
const telCollection: RadioItem[] = [{ label: 'Teléfono principal' }, { label: 'Personalizado' }]
const girCollection: RadioItem[] = [{ label: 'Giro' }, { label: 'No mostrar' }]

const Templates: React.FC = () => {
  const [clickedIndex, setClickedIndex] = useState<number>(-1)

  return (
    <ScreenWrapper>
      <div className="flex flex-col  p-4 bg-gray-100">
        <header>
          <h1 className="text-2xl font-bold">{'Gestionar Plantillas'}</h1>
        </header>
      </div>
      <div className="mx-auto ">
        <ButtonMenuSet
          captions={buttonCaptions}
          clickedIndex={clickedIndex}
          handleButtonClick={(n) => setClickedIndex(n)}
        />

        <div className="mt-4">
          {clickedIndex === 0 && <General setClickedIndex={setClickedIndex} />}
          {clickedIndex === 1 && <TemplateConsignment setClickedIndex={setClickedIndex} />}
          {clickedIndex === 2 && <TemplateDoc setClickedIndex={setClickedIndex} docType={AppPath.Reservations} />}
          {clickedIndex === 3 && <TemplateDoc setClickedIndex={setClickedIndex} docType={AppPath.Estimates} />}
          {clickedIndex === 4 && <TemplateDoc setClickedIndex={setClickedIndex} docType={AppPath.SaleNotes} />}
        </div>
      </div>
    </ScreenWrapper>
  )
}
export default Templates

const General: React.FC<{ setClickedIndex: React.Dispatch<React.SetStateAction<number>> }> = ({ setClickedIndex }) => {
  const [nomIndex, setNomIndex] = useState<number>(-1)
  const [telIndex, setTelIndex] = useState<number>(-1)
  const [girIndex, setGirIndex] = useState<number>(-1)
  const [nomPersonalizado, setNomPersonalizado] = useState<string>('')
  const [telPersonalizado, setTelPersonalizado] = useState<string>('')
  const { fetchTemplateOrg } = useTemplateFetcher()
  const { postTemplateGen } = useTemplatePoster()
  const { userData } = useAuth()
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true)
  const [templateOrg, setTemplateOrg] = useState<TemplateOrganization>()
  // Errores de validacion de los input
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  const [infoMessage, setInfoMessage] = useState<string>('')
  const nombreFantasia = userData?.organization?.name ?? ''
  const razonSocial = userData?.organization?.legalName ?? ''
  const giro = userData?.organization?.business ?? ''
  const telefono = userData?.organization?.phone ?? ''
  const NOM_FANTASIA = 0
  const NOM_LEGAL = 1
  const NOM_PERSONALIZADO = 2
  const TEL_PERSONALIZADO = 1

  useEffect(() => {
    const fetchDataAsync = async () => {
      const template: TemplateDto | null = await fetchTemplateOrg(userData?.organization?._id ?? '')

      const org = template?.general.organization
      setTemplateOrg(org)

      const nameType = org?.name.type

      if (nameType === '.') {
        setNomPersonalizado(org?.name.value ?? '')
        setNomIndex(NOM_PERSONALIZADO)
      } else if (nameType === '.legalName') {
        setNomIndex(NOM_LEGAL)
      } else {
        setNomIndex(NOM_FANTASIA)
      }

      const phoneType = org?.phone.type

      if (phoneType === '.') {
        setTelPersonalizado(org?.phone.value ?? '')
        setTelIndex(TEL_PERSONALIZADO)
      } else {
        setTelIndex(0)
      }

      const businessType = org?.business.type

      if (businessType === '-') {
        setGirIndex(1)
      } else {
        setGirIndex(0)
      }
    }
    fetchDataAsync()
  }, [])

  const handleStoreClick = async () => {
    if (buttonDisabled) return

    if (!templateOrg) return

    const fieldsToValidate = []

    if (nomIndex === NOM_PERSONALIZADO) {
      fieldsToValidate.push({ name: 'nomPersonalizado', value: nomPersonalizado, rule: Rule.REQUIRED })
    }

    if (telIndex === TEL_PERSONALIZADO) {
      fieldsToValidate.push({ name: 'telPersonalizado', value: telPersonalizado, rule: Rule.REQUIRED })
    }
    const validationErrors = validateFields(fieldsToValidate)

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)

      return
    }
    setErrors({})

    if (nomIndex === NOM_FANTASIA) {
      templateOrg.name.type = '.name'
    } else if (nomIndex === NOM_LEGAL) {
      templateOrg.name.type = '.legalName'
    } else {
      templateOrg.name.type = '.'
      templateOrg.name.value = nomPersonalizado
    }

    if (telIndex === TEL_PERSONALIZADO) {
      templateOrg.phone.type = '.'
      templateOrg.phone.value = telPersonalizado
    } else {
      templateOrg.phone.type = '.phone'
    }

    if (girIndex === 0) {
      templateOrg.business.type = '.business'
    } else {
      templateOrg.business.type = '-'
    }

    const id = await postTemplateGen(templateOrg)

    if (id) {
      setButtonDisabled(true)
      setInfoMessage('Plantilla grabada.')
    }
  }

  const handleCancelClick = async () => {
    setClickedIndex(-1)
  }

  return (
    <>
      <div className="flex">
        <RadioGroup
          collection={nombreCollection}
          name={'nombreOrg'}
          direction={'vertical'}
          checked={nomIndex}
          callback={(idx) => {
            setNomIndex(idx)
            setButtonDisabled(false)
          }}
          label="Selecciona el nombre de la organización"
          className="flex items-center space-x-2 mr-10 mb-4"
        />

        <div className="flex-col ml-4 w-3/12">
          <div className="mb-4 mt-8">
            <DisplayLabel label={nombreFantasia} />
          </div>
          <div className="mb-1">
            <DisplayLabel label={razonSocial} />
          </div>
          <InputField
            label=""
            value={nomPersonalizado}
            name="nomPersonalizado"
            type="string"
            handleInputChange={(e) => {
              if (nomIndex === NOM_PERSONALIZADO) {
                setNomPersonalizado(e.target.value)
              }
              setButtonDisabled(false)
            }}
            errors={errors}
          />
        </div>
      </div>
      <div className="flex mt-4">
        <RadioGroup
          collection={telCollection}
          name={'telOrg'}
          direction={'vertical'}
          checked={telIndex}
          callback={(idx) => {
            setTelIndex(idx)
            setButtonDisabled(false)
          }}
          label="Selecciona el teléfono de la organización"
          className="flex items-center space-x-2 mr-10 mb-4"
        />
        <div className="flex-col ml-4">
          <div className="mb-1 mt-8">
            <DisplayLabel label={telefono} />
          </div>

          <InputField
            label=""
            value={telPersonalizado}
            name="telPersonalizado"
            type={'string'}
            handleInputChange={(e) => {
              if (telIndex === TEL_PERSONALIZADO) {
                setTelPersonalizado(e.target.value)
              }
              setButtonDisabled(false)
            }}
            errors={errors}
          />
        </div>
      </div>

      <div className="flex mt-4">
        <RadioGroup
          collection={girCollection}
          name={'girOrg'}
          direction={'vertical'}
          checked={girIndex}
          callback={(idx) => {
            setGirIndex(idx)
            setButtonDisabled(false)
          }}
          label="Selecciona el giro de la organización"
          className="flex items-center space-x-2 mr-10 mb-4"
        />
        <div className="flex-col ml-4">
          <div className="mb-1 mt-8">
            <DisplayLabel label={giro} />
          </div>
        </div>
      </div>
      <div className="flex justify-center mt-4">
        <ButtonAction label="Grabar" handleButtonClick={handleStoreClick} disabled={buttonDisabled} />
        <div className="ml-4">
          <ButtonAction label="Cerrar" handleButtonClick={handleCancelClick} />
        </div>
      </div>
      <div className="flex justify-center mt-4">
        <Info message={infoMessage} trigger={Date.now()} onTimeout={() => setInfoMessage('')} noCenter={true} />
      </div>
    </>
  )
}
